import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  SprayCan, Brush, Droplets, CupSoda, Home as HomeIcon, Package, HardHat, Gift, Truck, RotateCcw, Award, Headphones,
  ArrowRight, Building2, FileSpreadsheet, RefreshCw, CheckSquare, FileText, CreditCard, PhoneCall, Cable, MapPin,
  ShieldCheck, Lock, Server, BadgeCheck, Star, Eye, Flame, Timer, Download, MessageSquare, ClipboardList, Percent, Users, Factory, Briefcase, Sparkles,
  ChevronLeft, ChevronRight
} from "lucide-react";
import { CATEGORIES, BRANDS, PRODUCTS, saleProducts, newProducts, HERO_IMAGES, INDUSTRIES, NEEDS, SCENARIOS, CLIENTS, TESTIMONIALS, demoStats, fmt, productById } from "../data/store";
import { ProductCard } from "../components/ProductCard";
import { Countdown, midnight } from "../components/Countdown";

const ICONS = { SprayCan, Brush, Droplets, CupSoda, Home: HomeIcon, Package, HardHat, Gift };
const CAMPAIGN_END = new Date("2026-07-31T23:59:59").getTime();

const H2 = ({ children }) => <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase">{children}</h2>;

/* ---------- 1. HERO (6 varianti) ---------- */
const HEROES = [
  { id: "enterprise", label: "Enterprise", overline: "Risinājums lieliem uzņēmumiem", title: "Centralizēts iepirkums visiem jūsu objektiem", text: "Vairāku adrešu piegādes, līguma cenas un ERP integrācija vienā platformā.", cta: "Pieteikt demo", link: "/vairumtirdznieciba", img: "warehouse" },
  { id: "procurement", label: "Procurement", overline: "Iepirkumu speciālistiem", title: "Augšupielādē SKU sarakstu — saņem piedāvājumu 1 dienā", text: "Ātra cenu salīdzināšana, apstiprināšanas plūsmas un pasūtījumu vēsture eksportam.", cta: "Saņemt cenu piedāvājumu", link: "/vairumtirdznieciba", img: "warehouse" },
  { id: "campaign", label: "Kampaņa", overline: "Piedāvājums līdz 31.07.2026", title: "Vasaras izpārdošana — atlaides līdz –25%", text: "Profesionālā ķīmija un higiēnas preces par kampaņas cenām. Godīgi: akcija beidzas norādītajā datumā.", cta: "Skatīt akcijas", link: "/akcijas", img: "cleaning", countdown: true },
  { id: "seasonal", label: "Sezonas", overline: "Sezonas aktualitāte", title: "Vasaras terases un pasākumu sezona klāt", text: "Vienreizlietojamie trauki, dzērienu glāzes un iepakojums āra pasākumiem.", cta: "Sezonas preces", link: "/kategorija/vienreizlietojamie-trauki", img: "cleaning" },
  { id: "personalized", label: "Personalizēts", overline: "Sveicināti atpakaļ!", title: "Jūsu biežāk pirktās preces atkal noliktavā", text: "Balstoties uz jūsu pirkumu vēsturi, esam sagatavojuši personalizētus ieteikumus.", cta: "Mani ieteikumi", link: "#ieteikumi", img: "warehouse" },
  { id: "b2b", label: "B2B klients", overline: "B2B konts · Demo uzņēmums SIA", title: "Jūsu līguma cenas ir aktīvas: –18% no bāzes cenas", text: "Kredītlimits: 4 500 € pieejams · 2 pasūtījumi gaida apstiprinājumu · Nākamā piegāde piektdien.", cta: "Atkārtot pēdējo pasūtījumu", link: "/kategorija/tirisanas-lidzekli", img: "warehouse" },
];

const Hero = () => {
  const [v, setV] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setV((p) => (p + 1) % HEROES.length), 5000);
    return () => clearInterval(t);
  }, [paused]);
  const h = HEROES[v];
  return (
    <section className="max-w-7xl mx-auto px-4 mt-4" data-testid="hero-section">
      <div className="relative overflow-hidden min-h-[320px] lg:min-h-[400px] flex group/hero"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <img key={`img-${h.id}`} src={HERO_IMAGES[h.img]} alt={h.title} className="absolute inset-0 w-full h-full object-cover animate-in fade-in duration-700" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/75 to-transparent" />
        <div key={h.id} className="relative z-10 p-8 lg:p-12 pb-14 flex flex-col justify-center max-w-2xl animate-in fade-in slide-in-from-left-4 duration-500">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF3B44] mb-3">{h.overline}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight uppercase" data-testid="hero-title">{h.title}</h1>
          <p className="text-white/80 mt-3 text-sm sm:text-base">{h.text}</p>
          {h.countdown && <div className="mt-4"><Countdown target={CAMPAIGN_END} testId="hero-countdown" /></div>}
          <div className="flex gap-3 mt-6">
            <Link to={h.link} data-testid="hero-cta" className="bg-[#D0121A] hover:bg-[#A80D14] text-white font-bold px-6 py-3 transition-colors">{h.cta}</Link>
            <Link to="/vairumtirdznieciba" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-6 py-3 transition-colors">Uzņēmumiem</Link>
          </div>
        </div>
        <button onClick={() => setV((v - 1 + HEROES.length) % HEROES.length)} data-testid="hero-prev" aria-label="Iepriekšējais"
          className="absolute left-3 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/25 text-white p-2.5 transition-colors opacity-0 group-hover/hero:opacity-100">
          <ChevronLeft size={20} />
        </button>
        <button onClick={() => setV((v + 1) % HEROES.length)} data-testid="hero-next" aria-label="Nākamais"
          className="absolute right-3 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-white/25 text-white p-2.5 transition-colors opacity-0 group-hover/hero:opacity-100">
          <ChevronRight size={20} />
        </button>
        <div className="absolute bottom-4 left-8 lg:left-12 z-20 flex gap-2" data-testid="hero-dots">
          {HEROES.map((x, i) => (
            <button key={x.id} onClick={() => setV(i)} data-testid={`hero-dot-${x.id}`} aria-label={x.label}
              className={`h-1.5 transition-all duration-300 ${i === v ? "w-8 bg-[#D0121A]" : "w-4 bg-white/40 hover:bg-white/70"}`} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- 2. KATEGORIJU BLOKI (4 skati) ---------- */
const CatSection = () => {
  const [tab, setTab] = useState("kategorija");
  const tabs = [
    { id: "kategorija", label: "Pēc kategorijas" },
    { id: "nozare", label: "Pēc nozares" },
    { id: "vajadziba", label: "Pēc vajadzības" },
    { id: "scenarijs", label: "Pēc scenārija" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 mt-14" data-testid="category-section">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <H2>Iepērcies ērtāk</H2>
        <div className="flex gap-2 flex-wrap">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`cat-tab-${t.id}`}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 border transition-colors ${tab === t.id ? "bg-[#D0121A] text-white border-[#D0121A]" : "border-[#E2E2E2] hover:border-[#111111]"}`}>{t.label}</button>
          ))}
        </div>
      </div>
      {tab === "kategorija" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="category-grid">
          {CATEGORIES.map((c, i) => {
            const Icon = ICONS[c.icon];
            return (
              <Link key={c.slug} to={`/kategorija/${c.slug}`} data-testid={`category-tile-${c.slug}`}
                className="group relative bg-white border border-[#E2E2E2] p-5 flex flex-col gap-3 hover:border-[#111111] hover:shadow-md transition-all duration-200">
                {i < 3 && <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider bg-[#111111] text-white px-1.5 py-0.5">Populāra</span>}
                {i === 7 && <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider bg-[#D0121A] text-white px-1.5 py-0.5">Jauna</span>}
                <span className="w-11 h-11 bg-[#F5F5F5] group-hover:bg-[#111111] flex items-center justify-center transition-colors">
                  <Icon size={22} className="text-[#111111] group-hover:text-white transition-colors" />
                </span>
                <span className="text-sm font-bold leading-snug">{c.name}</span>
                <span className="text-xs text-[#6B6B6B]">{c.subs.length} apakškategorijas</span>
              </Link>
            );
          })}
        </div>
      )}
      {tab === "nozare" && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="industry-grid">
          {INDUSTRIES.map((x, i) => {
            const Icon = [Briefcase, Building2, Users, Factory, Brush, Package][i];
            return (
              <Link key={x.name} to={`/kategorija/${x.cat}`} className="group bg-[#F5F5F5] p-5 flex gap-4 items-start hover:bg-[#111111] transition-colors">
                <Icon size={24} className="text-[#D0121A] shrink-0" />
                <div>
                  <div className="text-sm font-bold group-hover:text-white transition-colors">{x.name}</div>
                  <div className="text-xs text-[#6B6B6B] group-hover:text-white/60 mt-0.5 transition-colors">{x.desc}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      {tab === "vajadziba" && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="need-grid">
          {NEEDS.map((x) => (
            <Link key={x.name} to={`/kategorija/${x.cat}`} className="border border-[#E2E2E2] p-5 text-sm font-bold hover:border-[#D0121A] hover:text-[#D0121A] transition-colors flex items-center justify-between">
              {x.name} <ArrowRight size={15} />
            </Link>
          ))}
        </div>
      )}
      {tab === "scenarijs" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="scenario-grid">
          {SCENARIOS.map((x) => (
            <Link key={x.name} to={`/kategorija/${x.cat}`} className="bg-[#F5F5F5] p-5 hover:bg-[#111111] group transition-colors">
              <Sparkles size={20} className="text-[#D0121A] mb-3" />
              <div className="text-sm font-bold group-hover:text-white transition-colors">{x.name}</div>
              <div className="text-xs text-[#6B6B6B] group-hover:text-white/60 mt-1 transition-colors">{x.desc}</div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

/* ---------- 3. DIENAS / NEDĒĻAS PIEDĀVĀJUMI ---------- */
const Deals = () => {
  const day = productById(7);
  const week = saleProducts().filter((p) => p.id !== 7).slice(0, 3);
  const stats = demoStats(day);
  return (
    <section className="max-w-7xl mx-auto px-4 mt-14 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="deals-section">
      <div className="border-2 border-[#D0121A] p-6 flex flex-col sm:flex-row gap-6" data-testid="deal-of-day">
        <img src={day.image} alt={day.name} className="w-full sm:w-44 h-44 object-cover bg-[#F5F5F5]" />
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#D0121A]"><Timer size={14} /> Dienas piedāvājums · beidzas šodien</div>
          <Link to={`/prece/${day.id}`} className="block text-lg font-bold mt-2 hover:text-[#D0121A]">{day.name}</Link>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-2xl font-extrabold text-[#D0121A]">{fmt(day.price)}</span>
            <span className="line-through text-[#6B6B6B]">{fmt(day.oldPrice)}</span>
            <span className="text-xs text-[#6B6B6B]">ar PVN</span>
          </div>
          <div className="text-xs text-[#6B6B6B] mt-1 flex items-center gap-1"><Flame size={12} className="text-[#D0121A]" /> Pārdoti {stats.soldWeek} gab. šonedēļ · {stats.viewers} cilvēki šobrīd skatās</div>
          <div className="mt-4"><Countdown target={midnight()} testId="deal-countdown" /></div>
        </div>
      </div>
      <div className="bg-[#F5F5F5] p-6" data-testid="deals-of-week">
        <div className="text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2"><Percent size={14} className="text-[#D0121A]" /> Nedēļas piedāvājumi · līdz svētdienai</div>
        <div className="space-y-3">
          {week.map((p) => (
            <Link key={p.id} to={`/prece/${p.id}`} className="flex items-center gap-3 bg-white border border-[#E2E2E2] p-3 hover:border-[#D0121A] transition-colors">
              <img src={p.image} alt="" className="w-12 h-12 object-cover bg-[#F5F5F5]" />
              <span className="text-sm font-semibold flex-1 line-clamp-1">{p.name}</span>
              <span className="text-sm font-extrabold text-[#D0121A]">{fmt(p.price)}</span>
              <span className="text-xs line-through text-[#6B6B6B]">{fmt(p.oldPrice)}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- 4. PRODUKTU KARUSEĻI (cilnes) ---------- */
const ProductTabs = () => {
  const [tab, setTab] = useState("bestselleri");
  const lists = {
    bestselleri: [...PRODUCTS].sort((a, b) => demoStats(b).soldWeek - demoStats(a).soldWeek).slice(0, 8),
    popularakas: [...PRODUCTS].sort((a, b) => b.rating - a.rating).slice(0, 8),
    jaunumi: newProducts(),
    akcijas: saleProducts().slice(0, 8),
  };
  const tabs = [["bestselleri", "Bestselleri"], ["popularakas", "Populārākās"], ["jaunumi", "Jaunumi"], ["akcijas", "Akcijas"]];
  return (
    <section className="max-w-7xl mx-auto px-4 mt-14" data-testid="product-tabs-section">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <H2>Preču izlase</H2>
        <div className="flex gap-2 flex-wrap">
          {tabs.map(([id, l]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`products-tab-${id}`}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 border transition-colors ${tab === id ? "bg-[#111111] text-white border-[#111111]" : "border-[#E2E2E2] hover:border-[#111111]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 [&>*]:shrink-0 [&>*]:w-[220px] sm:[&>*]:w-[240px]">
        {lists[tab].map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </section>
  );
};

/* ---------- 5. DRĪZ BEIGSIES NOLIKTAVĀ ---------- */
const LowStock = () => {
  const low = PRODUCTS.filter((p) => demoStats(p).low).slice(0, 4);
  return (
    <section className="bg-[#F5F5F5] mt-14 py-12" data-testid="low-stock-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-1"><Flame size={20} className="text-[#D0121A]" /><H2>Drīz beigsies noliktavā</H2></div>
        <p className="text-sm text-[#6B6B6B] mb-5">Godīga informācija: atlikumi ir reāli ierobežoti — papildinājums plānots nākamnedēļ.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {low.map((p) => {
            const s = demoStats(p);
            return (
              <Link key={p.id} to={`/prece/${p.id}`} className="bg-white border border-[#E2E2E2] p-4 hover:border-[#D0121A] transition-colors" data-testid={`low-stock-${p.id}`}>
                <div className="flex gap-3">
                  <img src={p.image} alt="" className="w-16 h-16 object-cover bg-[#F5F5F5]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold line-clamp-2">{p.name}</div>
                    <div className="text-sm font-extrabold mt-1">{fmt(p.price)} <span className="text-[10px] font-normal text-[#6B6B6B]">ar PVN</span></div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1"><span className="font-bold text-[#D0121A]">Palicis: {s.stockLevel} gab.</span><span className="text-[#6B6B6B]">Pārdoti {s.soldWeek}/ned.</span></div>
                  <div className="h-1.5 bg-[#E2E2E2]"><div className="h-full bg-[#D0121A]" style={{ width: `${Math.min(100, s.stockLevel * 8)}%` }} /></div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- 6. IETEIKUMI + NESEN SKATĪTĀS ---------- */
const Recommendations = () => {
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    try { setRecent((JSON.parse(localStorage.getItem("demo_recent")) || []).map(productById).filter(Boolean)); } catch { /* noop */ }
  }, []);
  const forYou = PRODUCTS.filter((p) => p.rating >= 4.5).slice(0, 4);
  const forCompany = PRODUCTS.filter((p) => p.packQty >= 20).slice(0, 4);
  return (
    <section id="ieteikumi" className="max-w-7xl mx-auto px-4 mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10" data-testid="recommendations-section">
      <div>
        <H2>Ieteikts Jums</H2>
        <p className="text-xs text-[#6B6B6B] mt-1 mb-4">Balstīts uz skatītajām precēm un pirkumu vēsturi (demo).</p>
        <div className="grid grid-cols-2 gap-4">{forYou.map((p) => <ProductCard key={p.id} product={p} />)}</div>
      </div>
      <div>
        <H2>Ieteikts Jūsu uzņēmumam</H2>
        <p className="text-xs text-[#6B6B6B] mt-1 mb-4">Vairumiepakojumi, ko bieži izvēlas līdzīgi uzņēmumi.</p>
        <div className="grid grid-cols-2 gap-4">{forCompany.map((p) => <ProductCard key={p.id} product={p} />)}</div>
      </div>
      {recent.length > 0 && (
        <div className="lg:col-span-2" data-testid="recently-viewed">
          <H2>Nesen skatītās</H2>
          <div className="flex gap-4 overflow-x-auto pb-2 mt-4 [&>*]:shrink-0 [&>*]:w-[220px]">
            {recent.slice(0, 6).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </section>
  );
};

/* ---------- 7. B2B BLOKI ---------- */
const B2B_CARDS = [
  { icon: Building2, t: "Pieprasīt B2B kontu", d: "Līguma cenas un pēcapmaksa 3 min pieteikumā" },
  { icon: Percent, t: "Saņemt vairumtirdzniecības cenu", d: "Individuālas atlaides no apjoma — līdz –30%" },
  { icon: FileSpreadsheet, t: "Augšupielādēt SKU sarakstu", d: "CSV/Excel imports — piedāvājums 1 darba dienā" },
  { icon: RefreshCw, t: "Atkārtot iepriekšējo pasūtījumu", d: "Viens klikšķis — viss grozā no vēstures" },
  { icon: CheckSquare, t: "Apstiprinājumi gaida lēmumu", d: "Vairāklīmeņu apstiprināšanas plūsmas komandām" },
  { icon: FileText, t: "Jūsu uzņēmuma līguma cenas", d: "Personalizēts cenrādis pēc pieslēgšanās" },
  { icon: CreditCard, t: "Pēcapmaksa un kredītlimits", d: "Rēķins 7–30 dienas, elastīgs kredītlimits" },
  { icon: PhoneCall, t: "Runāt ar pārdošanas speciālistu", d: "Personīgais menedžeris katram B2B klientam" },
  { icon: Cable, t: "ERP integrācijas iespējas", d: "API, EDI, e-rēķini — savienojam ar jūsu sistēmu" },
  { icon: MapPin, t: "Pasūtījumi vairākām adresēm", d: "Viena pasūtījuma piegāde vairākiem objektiem" },
];

const B2BSection = () => (
  <section className="mt-14 bg-[#111111] py-12" data-testid="b2b-section">
    <div className="max-w-7xl mx-auto px-4">
      <span className="text-xs font-bold uppercase tracking-widest text-[#FF3B44]">B2B risinājumi</span>
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mt-1 mb-6">Uzņēmumiem un vairumtirdzniecībai</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {B2B_CARDS.map((x) => (
          <button key={x.t} onClick={() => toast.info(x.t, { description: "DEMO funkcija — pilnajā versijā šeit atvērtos attiecīgā B2B sadaļa." })}
            data-testid={`b2b-card-${x.t.split(" ")[0].toLowerCase()}`}
            className="text-left bg-white/5 border border-white/10 p-4 hover:bg-white/10 hover:border-[#D0121A] transition-colors group">
            <x.icon size={20} className="text-[#FF3B44] mb-3" />
            <div className="text-sm font-bold text-white leading-snug">{x.t}</div>
            <div className="text-xs text-white/50 mt-1">{x.d}</div>
          </button>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- 8. TRUST BLOKI ---------- */
const TrustSection = () => (
  <section className="max-w-7xl mx-auto px-4 mt-14" data-testid="trust-section">
    <H2>Mums uzticas</H2>
    <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mt-5" data-testid="client-logos">
      {CLIENTS.map((c) => (
        <div key={c} className="border border-[#E2E2E2] h-16 flex items-center justify-center text-sm font-extrabold tracking-tight text-[#6B6B6B] px-2 text-center">{c}</div>
      ))}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8" data-testid="testimonials">
      {TESTIMONIALS.map((t) => (
        <figure key={t.name} className="bg-[#F5F5F5] p-6">
          <div className="flex gap-0.5 mb-3">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className="fill-[#E5A000] text-[#E5A000]" />)}</div>
          <blockquote className="text-sm leading-relaxed">“{t.text}”</blockquote>
          <figcaption className="mt-4 text-xs"><span className="font-bold">{t.name}</span> · <span className="text-[#6B6B6B]">{t.role}</span></figcaption>
        </figure>
      ))}
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-8" data-testid="trust-badges">
      {[
        { icon: BadgeCheck, t: "Sertificētas preces", d: "CE un ES atbilstība" },
        { icon: Lock, t: "Droši maksājumi", d: "Šifrēti banku maksājumi" },
        { icon: Truck, t: "Piegādes partneri", d: "DPD, Omniva, Venipak (demo)" },
        { icon: ShieldCheck, t: "Garantijas un atgriešana", d: "14 dienu atgriešana bez jautājumiem" },
        { icon: Server, t: "Datu drošība un uptime", d: "99,9% pieejamība · GDPR · 24/7 atbalsts" },
      ].map((x) => (
        <div key={x.t} className="flex gap-3 items-start border border-[#E2E2E2] p-4">
          <x.icon size={20} className="text-[#D0121A] shrink-0" />
          <div>
            <div className="text-xs font-bold">{x.t}</div>
            <div className="text-[11px] text-[#6B6B6B] mt-0.5">{x.d}</div>
          </div>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="trust-bar">
      {[
        { icon: Truck, t: "Ātra piegāde", d: "Kurjers, pakomāts vai demo veikals" },
        { icon: RotateCcw, t: "14 dienu atgriešana", d: "Vienkārša preču atgriešana" },
        { icon: Award, t: "20+ gadu pieredze digitālos risinājumos", d: "Uzticams tehnoloģiju partneris" },
        { icon: Headphones, t: "Klientu atbalsts", d: "Darba dienās 8:00–17:00" },
      ].map((x) => (
        <div key={x.t} className="flex gap-3 items-start bg-[#F5F5F5] p-5">
          <x.icon size={24} className="text-[#111111] shrink-0" />
          <div>
            <div className="text-sm font-bold">{x.t}</div>
            <div className="text-xs text-[#6B6B6B] mt-0.5">{x.d}</div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- 9. LEAD GENERATION ---------- */
const LEADS = [
  { icon: Eye, t: "Pieteikt demo", d: "30 min platformas demonstrācija jūsu komandai" },
  { icon: MessageSquare, t: "Saņemt konsultāciju", d: "Palīdzēsim izvēlēties pareizos produktus" },
  { icon: Building2, t: "Pieteikt B2B kontu", d: "Līguma cenas un pēcapmaksa" },
  { icon: Download, t: "Lejupielādēt katalogu", d: "PDF katalogs ar visām cenām" },
  { icon: FileText, t: "Saņemt cenu piedāvājumu", d: "Individuāls piedāvājums 1 darba dienā" },
  { icon: ClipboardList, t: "Sezonas iepirkumu saraksts", d: "Gatavs čeklists jūsu nozarei" },
];

const LeadGen = () => (
  <section className="max-w-7xl mx-auto px-4 mt-14" data-testid="leadgen-section">
    <H2>Sāksim sadarbību</H2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
      {LEADS.map((x) => (
        <button key={x.t} onClick={() => toast.success(x.t, { description: "DEMO — pieteikums reģistrēts. Pilnajā versijā šeit atvērtos forma vai fails." })}
          data-testid={`lead-card-${x.t.split(" ")[0].toLowerCase()}`}
          className="text-left border border-[#E2E2E2] p-5 flex gap-4 items-start hover:border-[#D0121A] hover:shadow-md transition-all group">
          <span className="w-10 h-10 bg-[#F5F5F5] group-hover:bg-[#D0121A] flex items-center justify-center transition-colors shrink-0">
            <x.icon size={18} className="text-[#111111] group-hover:text-white transition-colors" />
          </span>
          <div>
            <div className="text-sm font-bold">{x.t}</div>
            <div className="text-xs text-[#6B6B6B] mt-0.5">{x.d}</div>
            <span className="text-xs font-bold text-[#D0121A] mt-2 inline-flex items-center gap-1">Pieteikties <ArrowRight size={12} /></span>
          </div>
        </button>
      ))}
    </div>
  </section>
);

/* ---------- ZĪMOLI ---------- */
const BrandsStrip = () => (
  <section className="max-w-7xl mx-auto px-4 mt-14" data-testid="brands-grid">
    <div className="flex items-end justify-between mb-5">
      <H2>Pērc pa zīmoliem</H2>
      <Link to="/zimoli" className="text-sm font-semibold text-[#111111] hover:underline flex items-center gap-1">Visi zīmoli <ArrowRight size={14} /></Link>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {BRANDS.map((b) => (
        <Link key={b.slug} to={`/zimoli#${b.slug}`} data-testid={`brand-tile-${b.slug}`}
          className="bg-white border border-[#E2E2E2] h-20 flex items-center justify-center text-lg font-extrabold tracking-tight text-[#6B6B6B] hover:text-[#D0121A] hover:border-[#D0121A] transition-all">{b.name}</Link>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <div>
      <Hero />
      <CatSection />
      <Deals />
      <ProductTabs />
      <LowStock />
      <Recommendations />
      <BrandsStrip />
      <B2BSection />
      <TrustSection />
      <LeadGen />
    </div>
  );
}
